import { Theme, css, useTheme } from '@emotion/react';

import BounceLoader from 'react-spinners/BounceLoader';
import SyncLoader from 'react-spinners/SyncLoader';

import { Radius, Spacing } from 'chatie-design-system';

import { safeArea } from '../../styles/css';
import { CSSProps } from '../../utils/misc';

const loadingCSS =
  (
    isFixed: boolean,
    isCenter: boolean,
    isBlocking: boolean,
    isPushed: boolean
  ) =>
  (theme: Theme) =>
    css`
      text-align: center;
      z-index: 5000;
      ${isPushed &&
      css`
        margin-top: ${Spacing.medium}px;
      `}
      ${!isCenter &&
      css`
        > div {
          padding: ${Spacing.xlarge}px ${Spacing.medium}px;
        }
      `}
        ${isBlocking &&
      css`
        background-color: ${theme.overlay.dark};

        > div {
          background-color: ${theme.var.white};
          border-radius: ${Radius.small}px;
        }
      `}
        ${isFixed &&
      css`
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 9999;
      `};
    `;

type Props = {
  fixed?: boolean;
  blocking?: boolean;
  push?: boolean;
  center?: boolean;
  size?: number;
};

const Loading = ({
  className,
  center = false,
  fixed = false,
  blocking = false,
  push = false,
  size = 10,
}: Props & CSSProps) => {
  const theme = useTheme();

  return (
    <div css={loadingCSS(fixed, center, blocking, push)} className={className}>
      <div className={fixed || center ? 'center' : undefined}>
        <SyncLoader
          color={theme?.colors?.main500 ?? '#20A2AE'}
          size={size}
          margin={5}
          speedMultiplier={0.6}
        />
      </div>
    </div>
  );
};

export default Loading;

const pageLoadingWrapCSS = (theme: Theme) => css`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: ${theme.name === 'dark' ? theme.overlay.dark : theme.overlay.light};
  z-index: 9999;
  ${safeArea('top')};
  ${safeArea('bottom', 'padding', Spacing.medium)};

  > div {
    width: 48px;
    height: 48px;
  }
`;

export const PageLoading = () => {
  const theme = useTheme();

  return (
    <div css={pageLoadingWrapCSS}>
      <div className="center">
        <BounceLoader
          color={theme.colors.main500}
          size={48}
          speedMultiplier={0.6}
        />
      </div>
    </div>
  );
};

const overlayLoadingCSS =
  (
    isFixed: boolean,
    isCenter: boolean,
    isBlocking: boolean,
    isPushed: boolean
  ) =>
  (theme: Theme) =>
    css`
      background-color: ${theme.overlay.dark};
      text-align: center;
      z-index: 5000;
      ${isPushed &&
      css`
        margin-top: ${Spacing.medium}px;
      `}
      ${!isCenter &&
      css`
        > div {
          padding: ${Spacing.xlarge}px ${Spacing.medium}px;
        }
      `}
        ${isBlocking &&
      css`
        background-color: ${theme.overlay.dark};

        > div {
          border-radius: ${Radius.small}px;
        }
      `}
        ${isFixed &&
      css`
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 9999;
      `};
    `;

export const OverlayLoading = ({
  className,
  center = false,
  fixed = false,
  blocking = false,
  push = false,
  size = 10,
}: Props & CSSProps) => {
  const theme = useTheme();

  return (
    <div
      css={overlayLoadingCSS(fixed, center, blocking, push)}
      className={className}
    >
      <div className={fixed || center ? 'center' : undefined}>
        <SyncLoader
          color={theme?.colors?.main500 ?? '#20A2AE'}
          size={size}
          margin={5}
          speedMultiplier={0.6}
        />
      </div>
    </div>
  );
};
